// The four lists the link picker offers, one per source module (W53.1 D-1).
//
// Split out of `EventLinkPickerSheet.tsx` for the same reason the sheet itself
// is separate from the route: the calendar detail page already sits near the
// 15 KB route ceiling (AGENTS.md §3 #10), and a picker that grew inside it would
// push the page over rather than the sheet.
//
// Each list is "a title and a tap". None of them needs to be rich — a link is a
// pointer, and the thing it points at is opened by the deep link afterwards — so
// they share one row shape instead of each module inventing a card. That is the
// deliberate opposite of `AlbumListRows`, which keeps its cover thumbnails
// because a photo picker is choosing *photos* and an album row has to be
// recognisable at a glance.

import { useQuery } from "@tanstack/react-query";
import { BookOpen, CalendarHeart, Star, Image as ImageIcon, type LucideIcon } from "lucide-react";

import { LoadingState } from "@/components/primitives/LoadingState";
import { listAnniversaries } from "@/lib/api/anniversary";
import { listDiaries } from "@/lib/api/diary";
import { listWishes } from "@/lib/api/wishlist";
import { useAlbumList } from "@/lib/hooks/album";
import { wishStatusLabel } from "@/lib/i18n/wishlist";
import { LINK_SOURCE_TYPES, type LinkSourceType } from "@/lib/i18n/calendar";
import { queryKeys } from "@/lib/queries/keys";

export interface LinkTargetListProps {
  type: LinkSourceType;
  enabled: boolean;
  onPick: (id: string) => void;
  busy?: boolean;
}

const ICON: Record<LinkSourceType, LucideIcon> = {
  album: ImageIcon,
  wish: Star,
  diary: BookOpen,
  anniversary: CalendarHeart,
};

const EMPTY_LABEL: Record<LinkSourceType, string> = {
  album: "還沒有相簿",
  wish: "還沒有心願",
  diary: "還沒有日記",
  anniversary: "還沒有紀念日",
};

interface Row {
  id: string;
  title: string;
  /** Trailing context — only some lists have anything worth saying. */
  detail?: string;
}

export function LinkTargetList({ type, enabled, onPick, busy = false }: LinkTargetListProps) {
  const { data, isLoading } = useLinkTargets(type, enabled);
  if (!enabled) return null;

  const Icon = ICON[type];
  const rows: Row[] = data ?? [];

  if (isLoading) return <LoadingState shape="row" count={3} />;
  if (rows.length === 0) {
    return (
      <p className="text-body-sm text-ink-2" data-testid={`link-target-empty-${type}`}>
        {EMPTY_LABEL[type]}
      </p>
    );
  }

  return (
    <ul className="flex flex-col divide-y divide-hairline" data-testid={`link-target-list-${type}`}>
      {rows.map((row) => (
        <li key={row.id}>
          <button
            type="button"
            disabled={busy}
            onClick={() => onPick(row.id)}
            className={`flex w-full items-center gap-s-3 py-s-3 text-left ${
              busy ? "opacity-50" : "hover:bg-black/5"
            }`}
            data-testid={`link-option-${type}-${row.id}`}
          >
            <Icon size={18} className="shrink-0 text-ink-3" aria-hidden />
            <span className="flex-1 truncate text-body text-ink-1">{row.title}</span>
            {row.detail ? (
              <span className="shrink-0 text-meta text-ink-3">{row.detail}</span>
            ) : null}
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Four queries behind one hook.
 *
 * All four endpoints already existed before this picker did — that is why D-1
 * needed no new backend route (§1.3 of the spec). `enabled` is false until the
 * sheet is open, so opening the calendar page does not fetch three lists nobody
 * is looking at.
 */
function useLinkTargets(type: LinkSourceType, enabled: boolean): {
  data: Row[] | undefined;
  isLoading: boolean;
} {
  const albums = useAlbumList({ enabled: enabled && type === "album" });

  // `["wishes", "list", …]` rather than a queryKeys entry: that is the key the
  // wishlist surface already invalidates on restore/undo, and prefix matching
  // means this list goes stale with it instead of drifting.
  const wishes = useQuery({
    queryKey: ["wishes", "list", "link-targets"],
    queryFn: () => listWishes({ limit: 50 }),
    enabled: enabled && type === "wish",
    staleTime: 30_000,
  });

  const diaries = useQuery({
    queryKey: [...queryKeys.diary.all, "link-targets"],
    queryFn: () => listDiaries(null, 50),
    enabled: enabled && type === "diary",
    staleTime: 30_000,
  });

  const anniversaries = useQuery({
    queryKey: [...queryKeys.anniversary.all, "link-targets"],
    queryFn: () => listAnniversaries({ limit: 50 }),
    enabled: enabled && type === "anniversary",
    staleTime: 30_000,
  });

  if (type === "album") {
    return {
      data: (albums.data ?? []).map((a) => ({
        id: a.id,
        title: a.name,
        detail: typeof a.item_count === "number" ? `${a.item_count} 張` : undefined,
      })),
      isLoading: albums.isLoading,
    };
  }
  if (type === "wish") {
    return {
      data: (wishes.data?.items ?? []).map((w) => ({
        id: w.id,
        title: w.name,
        detail: wishStatusLabel(w.status),
      })),
      isLoading: wishes.isLoading,
    };
  }
  if (type === "diary") {
    return {
      data: (diaries.data?.items ?? []).map((d) => ({
        id: d.id,
        title: d.title || d.content.slice(0, 30),
        detail: d.date,
      })),
      isLoading: diaries.isLoading,
    };
  }
  return {
    data: (anniversaries.data?.items ?? []).map((a) => ({
      id: a.id,
      title: a.title,
      detail: a.date,
    })),
    isLoading: anniversaries.isLoading,
  };
}

export { LINK_SOURCE_TYPES };
